import clsx from 'clsx'
import {
  forwardRef,
  type CSSProperties,
  type KeyboardEventHandler,
  type PointerEventHandler,
} from 'react'
import type { MatchMode, MatchType, SearchOrder } from '../../tables/logic/tableSearch'

const MATCH_TYPES: { value: MatchType; label: string }[] = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
]

const SEARCH_ORDERS: { value: SearchOrder; label: string }[] = [
  { value: 'rows', label: 'Across rows' },
  { value: 'columns', label: 'Down columns' },
]

function Chevron({ direction }: { direction: 'up' | 'down' }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d={direction === 'up' ? 'M3 7.5L6 4.5L9 7.5' : 'M3 4.5L6 7.5L9 4.5'}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M2.5 6.5L5 9L9.5 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function MatchOptionsIcon({ className }: { className?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <line
        x1="1.5"
        y1="4.5"
        x2="12.5"
        y2="4.5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <line
        x1="1.5"
        y1="9.5"
        x2="12.5"
        y2="9.5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <circle cx="9" cy="4.5" r="1.7" fill="currentColor" />
      <circle cx="5" cy="9.5" r="1.7" fill="currentColor" />
    </svg>
  )
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="hover:bg-muted flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left"
    >
      <span className="text-default text-sm">{label}</span>
      <span
        className={clsx(
          'relative h-4 w-7 shrink-0 rounded-full transition-colors',
          checked ? 'bg-[color:var(--control-on)]' : 'bg-[color:var(--border-strong)]'
        )}
      >
        <span
          className={clsx(
            // eslint-disable-next-line no-restricted-syntax -- the thumb is white in both themes, as on ToggleSwitch
            'absolute top-0.5 h-3 w-3 rounded-full bg-white shadow-sm transition-transform',
            checked ? 'translate-x-[14px]' : 'translate-x-0.5'
          )}
        />
      </span>
    </button>
  )
}

interface SearchMatchOptionsPanelProps {
  mode: MatchMode
  matchTypeOpen: boolean
  onModeChange: (partial: Partial<MatchMode>) => void
  onMatchTypeOpenChange: (open: boolean) => void
  className?: string
  style?: CSSProperties
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>
  onPointerDown?: PointerEventHandler<HTMLDivElement>
}

const SearchMatchOptionsPanel = forwardRef<HTMLDivElement, SearchMatchOptionsPanelProps>(
  function SearchMatchOptionsPanel(
    {
      mode,
      matchTypeOpen,
      onModeChange,
      onMatchTypeOpenChange,
      className,
      style,
      onKeyDown,
      onPointerDown,
    },
    ref
  ) {
    return (
      <div
        ref={ref}
        role="dialog"
        aria-label="Match options"
        style={style}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        className={clsx(
          'bg-default border-default shadow-lg w-60 rounded-lg border p-1.5',
          className
        )}
      >
        <ToggleRow
          label="Match exact text"
          checked={mode.exactText}
          onChange={(v) => onModeChange({ exactText: v })}
        />
        <ToggleRow
          label="Match whole word"
          checked={mode.wholeWord}
          onChange={(v) => onModeChange({ wholeWord: v })}
        />
        <ToggleRow
          label="Match case"
          checked={mode.caseSensitive}
          onChange={(v) => onModeChange({ caseSensitive: v })}
        />

        <div className="border-default my-1 border-t" />

        <div className="px-2 py-1.5">
          <div className="text-muted mb-1 text-xs font-medium">Search order</div>
          <div className="bg-muted border-default grid grid-cols-2 rounded-md border p-0.5">
            {SEARCH_ORDERS.map((order) => {
              const selected = mode.order === order.value
              return (
                <button
                  key={order.value}
                  type="button"
                  onClick={() => onModeChange({ order: order.value })}
                  className={clsx(
                    'h-7 rounded px-2 text-xs font-medium transition-colors',
                    selected
                      ? 'surface-default text-default shadow-sm'
                      : 'text-subtle hover:text-default'
                  )}
                >
                  {order.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="border-default my-1 border-t" />

        <button
          type="button"
          onClick={() => onMatchTypeOpenChange(!matchTypeOpen)}
          aria-expanded={matchTypeOpen}
          className="hover:bg-muted flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left"
        >
          <span className="text-default text-sm">Match type</span>
          <span className="text-muted flex items-center gap-1 text-xs">
            {MATCH_TYPES.find((t) => t.value === mode.type)?.label}
            <Chevron direction={matchTypeOpen ? 'up' : 'down'} />
          </span>
        </button>

        {matchTypeOpen && (
          <div className="mt-0.5">
            {MATCH_TYPES.map((t) => {
              const selected = mode.type === t.value
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => {
                    onModeChange({ type: t.value })
                    onMatchTypeOpenChange(false)
                  }}
                  className={clsx(
                    'hover:bg-muted flex w-full items-center justify-between rounded-md py-1.5 pl-5 pr-2 text-left text-sm',
                    selected ? 'text-default font-medium' : 'text-subtle'
                  )}
                >
                  {t.label}
                  {selected && (
                    <span className="text-[color:var(--control-on)]">
                      <CheckIcon />
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>
    )
  }
)

export default SearchMatchOptionsPanel
